import { showError, showHttpError, showSuccess } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { HtmlParser } from '@calendly/ui/utils/html-parser'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import moment from 'moment-timezone'

import { setFormErrors } from 'actions/form'
import { getUser, patchCurrentUserAfterProfileSave, setOrganization, setUser } from 'common'
import changeSet from 'core/change_set'
import { rootApi } from 'root-api'
import { loadLocale, momentLocaleFor } from 'services/load_locale'

import {
  accountUpdateRequest,
  getMemberships,
  getUserSettings,
  handleSmsLegalDisclaimer,
  patchUserSettings,
  submitCredentialsUpdate,
  submitUnlinkRequestCall,
  submitUnlinkVerificationCodeCall,
  transferOwnership
} from './api'
import {
  LOGIN_STATE_AUTH_POPUP,
  LOGIN_STATE_EMAIL_POPUP,
  LOGIN_STATE_EMAIL_SUCCESS_POPUP,
  LOGIN_STATE_PASSWORD_POPUP,
  LOGIN_STATE_UNLINK_POPUP,
  REQUEST_STATUS_COMPLETED,
  REQUEST_STATUS_INITIAL,
  REQUEST_STATUS_PENDING,
  REQUEST_STATUS_SUCCESS,
  UNLINK_POPUP_PAGE_INFO,
  UNLINK_POPUP_PAGE_SET_PASSWORD,
  UNLINK_POPUP_PAGE_VERIFICATION
} from './constants'
import { deletionRedirectToSelector } from './selectors'

export const fetchSettings = createAsyncThunk(
  'accountSettings/fetch',
  async (_, { dispatch, rejectWithValue }) => {
    const [
      [settingsSuccess, settingsResponse],
      [brandingSuccess, brandingResponse]
    ] = await Promise.all([
      httpRequest({ url: '/api/account/settings' }),
      httpRequest({ url: '/api/account/branding_settings' })
    ])

    if (!settingsSuccess) {
      dispatch(showHttpError(settingsResponse))
      return rejectWithValue(null)
    }
    if (!brandingSuccess) {
      dispatch(showHttpError(brandingResponse))
      return rejectWithValue(null)
    }

    return { ...settingsResponse, branding: brandingResponse }
  }
)

export const saveBrandingSettings = createAsyncThunk(
  'accountSettings/saveBranding',
  async ({ logo, cropData, enforceLogo, removeLogo, useOwnerCalendlyBranding, showCalendlyBranding }, { dispatch, rejectWithValue }) => {
    const formData = new FormData()

    if (logo instanceof File) {
      if (cropData) {
        formData.append('logo_crop', JSON.stringify(cropData))
      }
      formData.append('logo', logo)
    }
    if (removeLogo) {
      formData.append('remove_logo', '1')
    }

    formData.append('enforce_logo', enforceLogo)
    formData.append('hide_calendly_branding', !showCalendlyBranding)
    formData.append('use_owner_calendly_branding', useOwnerCalendlyBranding)

    const [success, response] = await httpRequest({
      url: '/api/account/branding_settings',
      method: 'PATCH',
      data: formData
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(null)
    }

    if (success) {
      dispatch(setOrganization({ enforce_logo: response.enforce_logo }))
      dispatch(setUser({ logo_url: response.logo_url, org_logo_url: response.org_logo_url }))
    }

    dispatch(
      showSuccess(
        I18n.t('publisher.flash.settings_saved')
      )
    )

    dispatch(setUser({ logo_url: response.logo_url }))

    return response
  }
)

export const saveOrganizationBrandingSettings = createAsyncThunk(
  'accountSettings/saveOrganizationBranding',
  async ({ name, logo, cropData, enforceLogo, removeLogo, branded, legalDisclaimer }, { dispatch, rejectWithValue }) => {
    const formData = new FormData()

    if (logo instanceof File) {
      if (cropData) {
        formData.append('logo_crop', JSON.stringify(cropData))
      }
      formData.append('logo', logo)
    }

    if (removeLogo) {
      formData.append('remove_logo', '1')
    }

    formData.append('enforce_logo', enforceLogo)
    formData.append('hide_calendly_branding', !branded)

    const requestOrgNameChange = name !== undefined
    const requestBrandingLogoChange = logo !== undefined || removeLogo !== undefined
    const requestSmsLegalDisclaimerChange = legalDisclaimer !== undefined

    const [
      [orgNameSuccess, orgNameResponse],
      [brandingSettingsSuccess, brandingSettingsResponse],
      [smsLegalDisclaimerSuccess, smsLegalDisclaimerResponse]
    ] = await Promise.all([
      requestOrgNameChange ? httpRequest({
        url: '/api/organization/name',
        method: 'PUT',
        params: { name }
      }) : [undefined, undefined],
      requestBrandingLogoChange ? httpRequest({
        url: '/api/account/branding_settings',
        method: 'PATCH',
        data: formData
      }) : [undefined, undefined],
      requestSmsLegalDisclaimerChange ? handleSmsLegalDisclaimer(legalDisclaimer) : [undefined, undefined]
    ])

    if (orgNameSuccess) {
      dispatch(setOrganization(orgNameResponse))
    }

    if (brandingSettingsSuccess) {
      dispatch(setOrganization({ enforce_logo: !!brandingSettingsResponse.org_logo_url }))
      dispatch(setUser({ logo_url: brandingSettingsResponse.logo_url, org_logo_url: brandingSettingsResponse.org_logo_url }))
    }

    const requestsMade = [orgNameSuccess, brandingSettingsSuccess, smsLegalDisclaimerSuccess].filter((success) => success !== undefined)
    const requestSuccesses = requestsMade.filter((success) => success)

    const allResponses = {
      ...orgNameResponse,
      ...brandingSettingsResponse,
      // If we made the PUT or DELETE request for the legal disclaimer, we need to return the new value or null (for deletion).
      // If we didn't make a request (nothing changed), we're okay to return undefined
      disclaimer: smsLegalDisclaimerResponse !== undefined ? (smsLegalDisclaimerResponse.disclaimer || null) : undefined,
    }

    if (requestSuccesses.length === requestsMade.length) {
      dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
      return allResponses
    }

    if (orgNameSuccess === false) { dispatch(showHttpError(orgNameResponse)) }
    if (brandingSettingsSuccess === false) { dispatch(showHttpError(brandingSettingsResponse)) }
    if (smsLegalDisclaimerSuccess === false) { dispatch(showHttpError(smsLegalDisclaimerResponse)) }
    return rejectWithValue(allResponses)
  }
)

export const fetchMemberships = createAsyncThunk(
  'accountSettings/fetchMemberships',
  async (query, { dispatch, rejectWithValue }) => {
    const [success, response] = await getMemberships(query)

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return { ...response }
  }
)

export const sendAccountDeletionCode = () => async (dispatch, rejectWithValue) => {
  const [success, response] = await httpRequest({
    url: '/api/account/account_deletion_email_verification/send_code',
    method: 'POST'
  })

  if (!success) {
    dispatch(showHttpError(response))
    return rejectWithValue([])
  }

  return response
}

export const fetchUserSettings = createAsyncThunk(
  'accountSettings/fetchUserSettings',
  async (_query, { dispatch, rejectWithValue }) => {
    const [success, response] = await getUserSettings()

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return response
  }
)

export const toggleSharedEventNotificationOption = createAsyncThunk(
  'accountSettings/toggleSharedEventNotificationOption',
  async (_query, { getState, dispatch, rejectWithValue }) => {
    const state = getState().account_settings.userSettings
    const { uuid, sharedEventNotificationEnabled } = state

    const data = {
      uuid,
      user_settings: {
        shared_event_notification_enabled: !sharedEventNotificationEnabled
      }
    }

    const [success, response] = await patchUserSettings(data)
    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return response
  }
)
const slice = createSlice({
  name: 'account_settings',
  initialState: {
    loginTab: {
      changedEmail: '',
      desiredAuth: '',
      popupState: null,
      unlinkPopupPage: UNLINK_POPUP_PAGE_INFO,
      unlinkVerificationCode: ''
    },
    isDataLoading: true,
    saveProfileRequestStatus: REQUEST_STATUS_INITIAL,
    profile: {
      name: '',
      slug: '',
      description: '',
      timezone: 'UTC',
      locale: 'en',
      avatar_url: '',
      email: '',
      authentication_title: '',
      authentication_name: '',
      user_locale: 'en',
      user_time_notation: '24h',
      user_country_code: 'US'
    },
    meta: {
      deletion: { redirect_to: '' }
    },
    branding: {
      requestStatus: REQUEST_STATUS_INITIAL,
      unbranded: false,
      hideCalendlyBranding: false,
      lastChangeCopy: null,
      canEnforceLogo: false,
      canUpdateLogo: false,
      smsLegalDisclaimer: null,
      logoUrl: null,
    },
    changeOwnerPopup: {
      members: [],
      isLoading: true,
      isLoadingOwnerChange: false,
      isCompleted: false,
    },
    userSettings: {
      uuid: '',
      sharedEventNotificationEnabled: true,
      isLoading: true
    }
  },
  reducers: {
    closeLoginPagePopup: (state) => {
      if (!state.loginTab) { state.loginTab = {} }
      state.loginTab.popupState = null
    },

    openAuthChangePopup: (state, { payload: desiredAuth }) => {
      if (!state.loginTab) { state.loginTab = {} }
      state.loginTab.popupState = LOGIN_STATE_AUTH_POPUP
      state.loginTab.desiredAuth = desiredAuth
    },

    openEmailUpdatePopup: (state) => {
      if (!state.loginTab) { state.loginTab = {} }
      state.loginTab.popupState = LOGIN_STATE_EMAIL_POPUP
    },

    openEmailUpdateSuccessPopup: (state) => {
      if (!state.loginTab) { state.loginTab = {} }
      state.loginTab.popupState = LOGIN_STATE_EMAIL_SUCCESS_POPUP
    },

    openPasswordUpdatePopup: (state) => {
      if (!state.loginTab) { state.loginTab = {} }
      state.loginTab.popupState = LOGIN_STATE_PASSWORD_POPUP
    },

    openUnlinkPopup: (state) => {
      if (!state.loginTab) { state.loginTab = {} }
      state.loginTab.popupState = LOGIN_STATE_UNLINK_POPUP
    },

    setChangedEmail: (state, { payload: email }) => {
      if (!state.loginTab) { state.loginTab = {} }
      state.loginTab.changedEmail = email
    },

    setUnlinkPopupPage: (state, { payload: page }) => {
      state.loginTab.unlinkPopupPage = page
    },

    setUnlinkVerificationCode: (state, { payload: code }) => {
      state.loginTab.unlinkVerificationCode = code
    },

    updateProfileRequestStarted: (state) => {
      state.saveProfileRequestStatus = REQUEST_STATUS_PENDING
    },

    updateProfileRequestCompleted: (state) => {
      state.saveProfileRequestStatus = REQUEST_STATUS_SUCCESS
    },
    toggleChangeOwnerPopupLoadingState: (state) => {
      state.changeOwnerPopup.isLoadingOwnerChange = !state.changeOwnerPopup.isLoadingOwnerChange
    },
    setIsOrgOwnerTransferComplete: (state) => {
      state.changeOwnerPopup.isCompleted = true
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSettings.pending, (state) => {
      state.isDataLoading = true
    })
    builder.addCase(fetchSettings.rejected, (state) => {
      state.isDataLoading = false
    })
    builder.addCase(fetchSettings.fulfilled, (state, { payload }) => {
      state.isDataLoading = false
      state.profile = payload.profile
      state.meta = payload.meta
      state.branding = {
        unbranded: payload.branding.unbranded,
        lastChangeCopy: payload.branding.last_change_copy,
        canEnforceLogo: payload.branding.policy.can_enforce_logo,
        canUpdateLogo: payload.branding.policy.can_update_logo,
        isProfileLogo: payload.branding.is_profile_logo,
        logoUrl: payload.branding.logo_url,
        smsLegalDisclaimer: payload.branding.sms_legal_disclaimer,
        useOwnerCalendlyBranding: payload.branding.use_owner_calendly_branding,
        hideCalendlyBranding: payload.branding.hide_calendly_branding
      }
    })
    builder.addCase(saveBrandingSettings.pending, (state) => {
      state.branding.requestStatus = REQUEST_STATUS_PENDING
    })
    builder.addCase(saveBrandingSettings.fulfilled, (state, { payload }) => {
      state.branding.unbranded = payload.unbranded
      state.branding.lastChangeCopy = payload.last_change_copy
      state.branding.logoUrl = payload.logo_url
      state.branding.requestStatus = REQUEST_STATUS_COMPLETED
    })
    builder.addCase(saveOrganizationBrandingSettings.pending, (state) => {
      state.branding.requestStatus = REQUEST_STATUS_PENDING
    })
    builder.addCase(saveOrganizationBrandingSettings.fulfilled, (state, { payload }) => {
      const { branding: { unbranded, lastChangeCopy, smsLegalDisclaimer } } = state
      const { unbranded: newUnbrandedSetting, last_change_copy: newLastChangeCopy, disclaimer: newLegalDisclaimer } = payload

      state.branding.unbranded = newUnbrandedSetting !== undefined ? newUnbrandedSetting : unbranded
      state.branding.lastChangeCopy = newLastChangeCopy !== undefined ? newLastChangeCopy : lastChangeCopy
      state.branding.requestStatus = REQUEST_STATUS_COMPLETED
      state.branding.smsLegalDisclaimer = newLegalDisclaimer !== undefined ? newLegalDisclaimer : smsLegalDisclaimer
    })
    builder.addCase(saveOrganizationBrandingSettings.rejected, (state, { payload }) => {
      const { disclaimer, unbranded, last_change_copy: lastChangeCopy } = payload
      // If something else in the branding form failed to save, we should still reflect any changes we succeeded in making
      if (disclaimer !== undefined) {
        state.branding.smsLegalDisclaimer = disclaimer
      }

      if (unbranded !== undefined) {
        state.branding.unbranded = unbranded
      }

      if (lastChangeCopy !== undefined) {
        state.branding.lastChangeCopy = lastChangeCopy
      }
    })
    builder.addCase(fetchMemberships.pending, (state) => {
      state.changeOwnerPopup.isLoading = true
    })
    builder.addCase(fetchMemberships.fulfilled, (state, { payload }) => {
      state.changeOwnerPopup.members = payload.results
      state.changeOwnerPopup.isLoading = false
    })
    builder.addCase(fetchMemberships.rejected, (state) => {
      state.changeOwnerPopup.isLoading = false
    })
    builder.addCase(fetchUserSettings.pending, (state) => {
      state.userSettings.isLoading = true
    })
    builder.addCase(fetchUserSettings.fulfilled, (state, { payload }) => {
      const {
        shared_event_notification_enabled: sharedEventNotificationEnabled,
        uuid
      } = payload

      state.userSettings.isLoading = false
      state.userSettings.uuid = uuid
      state.userSettings.sharedEventNotificationEnabled = sharedEventNotificationEnabled
    })
    builder.addCase(fetchUserSettings.rejected, (state) => {
      state.userSettings.isLoading = false
    })
    builder.addCase(toggleSharedEventNotificationOption.pending, (state) => {
      state.userSettings.isLoading = true
    })
    builder.addCase(toggleSharedEventNotificationOption.fulfilled, (state, { payload }) => {
      const { shared_event_notification_enabled: sharedEventNotificationEnabled } = payload

      state.userSettings.isLoading = false
      state.userSettings.sharedEventNotificationEnabled = sharedEventNotificationEnabled
    })
    builder.addCase(toggleSharedEventNotificationOption.rejected, (state) => {
      state.userSettings.isLoading = false
    })
  }
})

const {
  actions: {
    openEmailUpdateSuccessPopup,
    setChangedEmail,
    setUnlinkVerificationCode,
    toggleChangeOwnerPopupLoadingState,
    setIsOrgOwnerTransferComplete,
    updateProfileRequestStarted,
    updateProfileRequestCompleted
  }
} = slice

export const {
  reducer,
  actions: {
    closeLoginPagePopup,
    openEmailUpdatePopup,
    openPasswordUpdatePopup,
    openAuthChangePopup,
    openUnlinkPopup,
    setUnlinkPopupPage
  }
} = slice

export const submitAccountDeletion = (values) => async (dispatch, getState) => {
  const [success, response] = await httpRequest({
    url: '/api/account/deletion',
    method: 'POST',
    data: values
  })

  if (success) {
    const redirectTo = deletionRedirectToSelector(getState())
    window.location.assign(redirectTo)
  } else {
    dispatch(showHttpError(response))
  }
}


const handleResponseStatus = ([success, response]) => (dispatch) => {
  if (success) {
    dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')))
  } else {
    dispatch(setFormErrors(response))
  }
}

export const submitSlugUpdate = (data) => async (dispatch) => {
  const result = await accountUpdateRequest(data)
  const [success] = result

  if (success) {
    dispatch(rootApi.util.invalidateTags(['CURRENT_USER']))
  }

  dispatch(handleResponseStatus(result))
  return success
}

const appendAvatarAttributes = (formData, { file, crop, remove }) => {
  if (file) {
    formData.append('avatar', file)
    if (crop) {
      formData.append('avatar_crop', JSON.stringify(crop))
    }
    return
  }

  if (remove) {
    formData.append('remove_avatar', '1')
  }
}

const buildFormData = ({
  avatar,
  country,
  language,
  dateNotation,
  timeNotation,
  ...data
}) => {
  const formData = new FormData()

  appendAvatarAttributes(formData, avatar)

  formData.append('user_country_code', country)
  formData.append('user_locale', language)
  formData.append('user_date_notation', dateNotation)
  formData.append('user_time_notation', timeNotation)

  Object.keys(data).forEach((key) => {
    formData.append(key, data[key])
  })

  return formData
}

export const submitProfileUpdate = (data) => async (dispatch, getState) => {
  const formData = buildFormData(data)
  const { data: previousUser } = getUser.select()(getState()) || {}
  const oldLocale = previousUser?.locale

  dispatch(updateProfileRequestStarted())
  const result = await accountUpdateRequest(formData)
  const [success, response] = result

  dispatch(handleResponseStatus(result))

  if (!success) { return }

  dispatch(updateProfileRequestCompleted())
  dispatch(patchCurrentUserAfterProfileSave(response, data))

  const newLocale = response?.user_locale ?? data.language ?? oldLocale

  if (newLocale != null && newLocale !== oldLocale) {
    try {
      await loadLocale(newLocale)
      moment.locale(momentLocaleFor(newLocale))
      I18n.locale = newLocale
    } catch {
      changeSet.reset()
      window.location.reload()
      return
    }
  }

  dispatch(rootApi.util.invalidateTags(['CURRENT_USER']))
  changeSet.reset()
}

export const submitEmailUpdate = ({ password, email }) => async (dispatch) => {
  const [success, response] = await submitCredentialsUpdate({
    current_password: password,
    email,
    flow: 'email'
  })

  if (success) {
    dispatch(setChangedEmail(email))
    dispatch(openEmailUpdateSuccessPopup())
    return true
  }

  const emailError = response.payload?.email
  if (emailError?.[0] === 'blocked_email') {
    emailError[0] = HtmlParser(I18n.t('publisher.account_settings.login.blocked_email', { support_url: 'mailto:support@calendly.com' }))
  }

  dispatch(setFormErrors(response, { current_password: 'password' }))
  return false
}

export const changeOwnership = (params) => async (dispatch, getState) => {
  dispatch(toggleChangeOwnerPopupLoadingState())
  const [success, response] = await transferOwnership(params)
  dispatch(toggleChangeOwnerPopupLoadingState())

  if (!success) {
    dispatch(showHttpError(response))
    return success
  }
  dispatch(setIsOrgOwnerTransferComplete())
  if (params.deleteAccount) {
    const redirectTo = deletionRedirectToSelector(getState())
    window.location.assign(redirectTo)
  }
  if (params.leaveOrganization) {
    window.location.reload()
  }

  if (params.deleteAccount) {
    const redirectTo = deletionRedirectToSelector(getState())
    window.location.assign(redirectTo)
  }
  if (params.leaveOrganization) {
    window.location.reload()
  }
  return success
}

export const submitPasswordUpdate = ({ currentPassword, newPassword, passwordConfirmation }) => async (dispatch) => {
  const [success, response] = await submitCredentialsUpdate({
    current_password: currentPassword,
    password: newPassword,
    password_confirmation: passwordConfirmation,
    flow: 'password'
  })

  if (success) {
    dispatch(showSuccess(I18n.t('publisher.account_settings.login.password_saved')))
    dispatch(closeLoginPagePopup())
    return
  }

  dispatch(setFormErrors(response, {
    current_password: 'currentPassword',
    password: 'newPassword',
    password_confirmation: 'passwordConfirmation',
  }))
}

export const submitUnlinkRequest = () => async (dispatch) => {
  const [success, response] = await submitUnlinkRequestCall()

  if (success) {
    dispatch(setUnlinkPopupPage(UNLINK_POPUP_PAGE_VERIFICATION))
    return
  }

  dispatch(showHttpError(response))
}

export const submitUnlinkVerificationCode = ({ code, setIsLoading }) => async (dispatch) => {
  const [success] = await submitUnlinkVerificationCodeCall(code)

  if (success) {
    dispatch(setUnlinkVerificationCode(code))
    dispatch(setUnlinkPopupPage(UNLINK_POPUP_PAGE_SET_PASSWORD))
    return
  }

  dispatch(showError(I18n.t('publisher.account_settings.login.unlink_verification_code_invalid')))
  setIsLoading(false)
}

export const submitUnlinkAndSetPassword = ({ newPassword, passwordConfirmation, verificationCode }) => async (dispatch) => {
  const [success, response] = await submitCredentialsUpdate({
    password: newPassword,
    password_confirmation: passwordConfirmation,
    verification_code: verificationCode,
    flow: 'oauth_removal_set_password'
  })

  if (success) {
    dispatch(showSuccess(I18n.t('publisher.account_settings.login.unlink_successful')))
    dispatch(closeLoginPagePopup())
    dispatch(rootApi.util.invalidateTags(['CURRENT_USER']))
    return
  }

  dispatch(setFormErrors(response, {
    password: 'newPassword',
    password_confirmation: 'passwordConfirmation',
  }))
}
